import React, { LegacyRef, forwardRef, use, useCallback, useEffect, useRef, useState } from 'react';
import { message, Select } from 'antd';
import cN from 'classnames';
import { CatalogCompanyType, CatalogItem } from '@/shared/types/catalog';
import { CustomizationType, IColor, ICustomization, IOption } from '@/shared/types/sourcing-marketplace';
import {
  getCatalogItemCustomizationArray,
  getCreatorHubProductDetailsPagePath,
  getSupplierName,
} from '@/shared/utils/sourcing-marketplace-util';
import {
  PDP_VARIANT_UUID_SEPARATOR,
  SAMPLE_QUANTITY_SELECTOR_ID,
  WORK_WITH_FACTORIES_DESCRIPTION,
} from '@/shared/constants/sourcing-marketplace';
import { logCommonView } from '@/lib/service';
import { EventSource } from '@/shared/constants/event';
import SafeImage from '@/components/Common/SafeImage';
import { formatCurrency } from '@/shared/utils/utils';
import Image from 'next/image';
import { CloseCircleFilled } from '@ant-design/icons';
import Link from 'next/link';
import ProductTitle from '../ProductTitle';
import NumericInput from '@/components/Common/NumericInput';
import { useCreateAccountProvider } from '@/lib/context';
import './index.less';
import {
  filter,
  forEach,
  get,
  isEmpty,
  isString,
  keys,
  lowerCase,
  map,
  omitBy,
  size,
  sum,
  take,
  uniq,
  values,
} from 'lodash-es';
import { useIsMobileURL } from '@/shared/hooks';
import ChatBubbleIcon from '@/components/Common/Icons/ChatBubbleIcon';
import TeamMeetingIcon from '@/components/Common/Icons/TeamMeetingIcon';

const DEFAULT_VARIANTS_TITLE = 'Variants';

interface IProps {
  item: CatalogItem;
  getPriceForVariant: (variantUuid?: string) => number;
  isVariantAvailable: (variantUuid?: string) => boolean;
  showProductTitle: boolean;
  displayProductRunCard?: boolean;
}

const SampleBuilderSection = forwardRef(
  ({ getPriceForVariant, isVariantAvailable, item, showProductTitle, displayProductRunCard }: IProps, ref) => {
    const [isQuantitySelectorExpanded, setQuantitySelectorExpanded] = useState<boolean>(false);
    const [customizationArray, setCustomizationArray] = useState<ICustomization[]>([]);
    const [showVariantsInDetail, setShowVariantsInDetail] = useState<boolean>(false);
    const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
    const [showSelectedVariants, setShowSelectedVariants] = useState<boolean>(false);
    const [currentInputInFocus, setCurrentInputInFocus] = useState<string>(null);
    const [selectedVariants, setSelectedVariants] = useState<{ [key: string]: number }>({});

    const { showSignupDialogFromSourcing } = useCreateAccountProvider();
    // const { isMobile } = useConfigProvider();
    const isMobile = useIsMobileURL();
    const sampleTimeRef = useRef(null);
    const samplePriceRef = useRef(null);

    const itemName = item?.name;
    const totalQty = sum(values(selectedVariants));
    const canAddToCart = totalQty > 0;
    const isQtyPickerDisabled = values(filter(selectedOptions, (o) => !!o)).length + 1 < customizationArray.length;
    const supplier = item?.catalogCompany;
    const isServices = supplier?.type === CatalogCompanyType.SPECIALIST;

    const isRequestAQuote = (item?.meta?.sampleOrderPrice ?? 0) === 0;
    const showNumericalInputs = !isRequestAQuote;
    const getColorName = (color: IColor) => (color.colorVariant ? `${color.name} (${color.colorVariant})` : color.name);
    const supplierProfileUrl = `/sourcing/suppliers/${supplier?.handle}`;

    const updateVariant = (variantUuid, quantity) => {
      if (quantity > 0) {
        setSelectedVariants({ ...selectedVariants, [variantUuid]: quantity });
      } else {
        setSelectedVariants(omitBy(selectedVariants, (qty, key) => key === variantUuid));
      }
    };

    const createUuidForVariant = (optionValues: string[]) => {
      const id = uniq(filter(optionValues, (v) => !!v)).join(PDP_VARIANT_UUID_SEPARATOR);
      return id || 'only-variant';
    };

    const openSignup = () => {
      showSignupDialogFromSourcing(
        WORK_WITH_FACTORIES_DESCRIPTION,
        getCreatorHubProductDetailsPagePath(item.id, 'source'),
        'product_details_page',
      );
    };

    const getCustomizationArray = useCallback(() => {
      if (isEmpty(Object.keys(item || {}))) {
        return [];
      }

      if (isEmpty(item?.customizations)) {
        return [
          {
            identifier: 'customization-0',
            title: 'Variants',
            type: 'tags' as CustomizationType,
            value: [itemName],
          },
        ];
      }

      const customizations = getCatalogItemCustomizationArray(item);

      const newSelectedOptions = {};
      forEach(customizations, (c) => (newSelectedOptions[c.identifier] = null));
      setSelectedOptions(newSelectedOptions);
      return customizations;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.id]);

    const setDefaultSelectedVariants = useCallback(() => {
      const defaultSelectedOptions = {
        ...selectedOptions,
      };
      forEach(customizationArray, (customization: ICustomization) => {
        const options = customizationToOptionsArray(customization);

        if (size(options) === 1) {
          defaultSelectedOptions[customization.identifier] = get(options, '[0].value');
        }
      });
      setSelectedOptions(defaultSelectedOptions);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customizationArray]);

    useEffect(() => {
      setCustomizationArray(getCustomizationArray());
    }, [getCustomizationArray]);

    useEffect(() => {
      setDefaultSelectedVariants();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customizationArray]);

    const selectOption = (customizationIdentifier: string, optionValue: string) => {
      const newSelectedOptions = {
        ...selectedOptions,
        [customizationIdentifier]: optionValue,
      };
      setSelectedOptions(newSelectedOptions);
    };

    const customizationToOptionsArray = (customization: ICustomization) =>
      map(
        customization?.value?.filter((x: any) => x),
        (option) => {
          if (customization.type === 'colors') {
            option.value = getColorName(option);
            option.color = option.hexCode;
          } else if (customization.type === 'image_opts') {
            option.value = option.name;
          } else {
            option = { value: option };
          }
          return option;
        },
      );

    const CustomizationSelector = ({ customization }: { customization: ICustomization }) => {
      const options = customizationToOptionsArray(customization);

      if (isEmpty(options)) return null;

      if (size(options) === 1) return <span className="value">{get(options, '[0].value')}</span>;

      return (
        <div className="customization-selector">
          <Select
            placeholder="Select one"
            onChange={(value) => {
              selectOption(customization.identifier, value);
            }}
            value={selectedOptions[customization.identifier]}
            getPopupContainer={(trigger) => trigger.parentNode}
            aria-label="Select color"
          >
            {map(options, (option) => (
              <Select.Option key={option.value} value={option.value}>
                {renderTitleDiv(option)}
              </Select.Option>
            ))}
          </Select>
        </div>
      );
    };

    const renderTitleDiv = (option: IOption) => {
      return (
        <div className="variant-title">
          {option.image && (
            <SafeImage className="catalog-item-variant-thumbnail" src={option.image} transform="compact" />
          )}
          {option.color && <span className="color" style={{ backgroundColor: option.color }} />}
          <span className="inner">{option.value}</span>
        </div>
      );
    };

    const renderNumericOption = (option: IOption, variantUuid) => {
      const sampleMoq = item?.meta?.sampleMOQ || 0;
      const currentCount = selectedVariants[variantUuid] || 0;

      return (
        <NumericInput
          autoFocus={currentInputInFocus === variantUuid}
          disabled={isQtyPickerDisabled}
          min={sampleMoq}
          step={1}
          value={currentCount}
          onBlur={() => setCurrentInputInFocus(null)}
          onFocus={() => setCurrentInputInFocus(variantUuid)}
          onChange={(quantity: number) => {
            if (!isQtyPickerDisabled) {
              updateVariant(variantUuid, quantity);
              logCommonView(EventSource.PRODUCT_DETAILS_SELECT_VARIANTS, item?.id);
            }
          }}
        />
      );
    };

    const renderPricingSection = (option: IOption, variantUuid: string) => {
      const price = getPriceForVariant(variantUuid);
      const isAvailable = isVariantAvailable(variantUuid);

      if (!isAvailable) {
        return <span className="price">Sold Out</span>;
      }

      if (!price) {
        return <span />;
      }

      return (
        <>
          <span className="price">
            {isServices && 'Starting at '} {formatCurrency(price)}
          </span>
          {!isServices && renderNumericOption(option, variantUuid)}
        </>
      );
    };

    const renderQtySelectorPricingSection = (list: IOption[]) => {
      return (
        <div className="qty-selector-section" ref={samplePriceRef}>
          {map(list, (option: IOption) => {
            const variantUuid = createUuidForVariant([...values(selectedOptions), option.value]);

            return (
              <div
                key={`${option}--${variantUuid}`}
                className="option-row"
                onClick={() => {
                  if (isQtyPickerDisabled) {
                    message.info(`Please choose an option for the section: ${customizationArray[0].title}`);
                  }
                }}
              >
                {renderTitleDiv(option)}
                <div className="right">{renderPricingSection(option, variantUuid)}</div>
              </div>
            );
          })}
        </div>
      );
    };

    const QtySelectorSection = ({ customization }: { customization: ICustomization }) => {
      const options = customizationToOptionsArray(customization);
      const maxToShow = 5;
      const firstFiveOptions = take(options, maxToShow);

      return (
        <>
          {renderQtySelectorPricingSection(isQuantitySelectorExpanded ? options : firstFiveOptions)}
          {size(options) > maxToShow && (
            <div className="see-more" onClick={() => setQuantitySelectorExpanded(!isQuantitySelectorExpanded)}>
              <span>See {isQuantitySelectorExpanded ? 'less' : 'more'}</span>
              <Image
                className={cN('arrow', { down: isQuantitySelectorExpanded })}
                width={16}
                height={16}
                src="/images/arrow_up.svg"
                alt="arrow"
              />
            </div>
          )}
        </>
      );
    };

    const VariantCustomization = ({
      customization,
      showQtySelector,
    }: {
      customization: ICustomization;
      showQtySelector: boolean;
    }) => {
      const title = customization?.title || DEFAULT_VARIANTS_TITLE;
      return (
        <div className="variant-section">
          <div className={cN('selector-box', { 'selector-box-full-width': showQtySelector })}>
            {title !== DEFAULT_VARIANTS_TITLE && <span className="title">{title}</span>}
            {showQtySelector ? (
              <QtySelectorSection customization={customization} />
            ) : (
              <CustomizationSelector customization={customization} />
            )}
          </div>
          {!showQtySelector && !isServices && (
            <div className="sample-time" ref={sampleTimeRef}>
              <span className="title">Sample time</span>
              {renderSampleTime()}
            </div>
          )}
        </div>
      );
    };

    const descriptionVariants = (
      <div className="description-variants">
        {customizationArray.map((variant) => {
          return (
            <div className="description-variant" key={`variant-values--${variant.title}`}>
              {lowerCase(variant.title) !== 'variants' && <span className="variant-name">{variant.title}: </span>}
              {variant.value
                .map((value) => {
                  return isString(value) ? value : get(value, 'name', '');
                })
                .join('/')}
            </div>
          );
        })}
      </div>
    );

    const itemContainsVariants = (): boolean => {
      const relevantCustomizations = customizationArray.filter((customization) => {
        return (customization?.title ?? '').toLowerCase() !== 'variants';
      });
      return !isEmpty(relevantCustomizations);
    };

    const getSubtotal = () => {
      return sum(
        map(selectedVariants, (qty, varUuid) => {
          return qty * getPriceForVariant(varUuid);
        }),
      );
    };

    const Description = () => {
      const description = item?.meta?.description;
      const includeVariants = itemContainsVariants() && isRequestAQuote;

      if (!description || !includeVariants) return null;
      return (
        <div className="variant-details">
          <div className="title" onClick={() => setShowVariantsInDetail(!showVariantsInDetail)}>
            Variants
            <Image
              className={cN('arrow', { down: showVariantsInDetail })}
              width={16}
              height={16}
              src="/images/arrow_up.svg"
              alt="arrow"
            />
          </div>
          {showVariantsInDetail && descriptionVariants}
        </div>
      );
    };

    const SelectedVariantsSection = () => {
      if (isEmpty(selectedVariants)) return null;

      const totalQty = sum(values(selectedVariants));
      return (
        <div className="selected-variants">
          <div className="header">
            <span className="left">
              {totalQty} sample{totalQty > 1 ? 's' : ''} selected
            </span>
            <div className="right" onClick={() => setShowSelectedVariants(!showSelectedVariants)}>
              <span>Subtotal: {formatCurrency(getSubtotal())}</span>
              <Image
                className={cN('arrow', { expanded: showSelectedVariants })}
                width={8}
                height={12}
                src="/images/arrow_left.png"
                alt="arrow"
              />
            </div>
          </div>
          {showSelectedVariants &&
            map(keys(selectedVariants), (variantUuid: string) => {
              const quantity = selectedVariants[variantUuid];
              const unitCost = getPriceForVariant(variantUuid);
              const itemTotalPrice = quantity * unitCost;
              const itemNameWithVariants = `${itemName} (${variantUuid.split(PDP_VARIANT_UUID_SEPARATOR).join(', ')})`;
              return (
                <div key={`variant--${variantUuid}--${quantity}`} className="variant">
                  <span>
                    {itemNameWithVariants} (Qty: {quantity})
                  </span>
                  <div>
                    <span className="price">{formatCurrency(itemTotalPrice)}</span>
                    <span>
                      <CloseCircleFilled
                        onClick={() => {
                          updateVariant(variantUuid, 0);
                        }}
                      />
                    </span>
                  </div>
                </div>
              );
            })}
        </div>
      );
    };

    const YouWillReceive = () => {
      const sampleOrder = item?.meta?.sampleOrder;
      if (isEmpty(sampleOrder)) {
        return null;
      }
      return (
        <span className="you-will-receive">
          You will receive:{' '}
          {!isEmpty(sampleOrder) ? sampleOrder : 'The product you have just designed with your customizations.'}
        </span>
      );
    };

    const renderSamplePrice = () => {
      if (!item?.displayPrice) {
        return (
          <span className="data">
            <Link href={supplierProfileUrl}>
              <span>Get quote</span>
            </Link>
          </span>
        );
      }

      return <span className="data">{formatCurrency(item?.displayPrice)}</span>;
    };

    const renderSampleTime = () => {
      const samplesEta = supplier?.samplesEta;
      if (!samplesEta) {
        return (
          <span className="data">
            <Link href={supplierProfileUrl}>
              <span>Ask supplier</span>
            </Link>
          </span>
        );
      }

      return (
        <span className="data">
          {samplesEta} day{samplesEta === 1 ? '' : 's'}
        </span>
      );
    };

    const SamplePricing = () =>
      !isServices && (
        <div className="sample-pricing-info">
          <div className="section">
            <h2 className="title">Sample cost</h2>
            {renderSamplePrice()}
          </div>
          <div className="section">
            <h2 className="title">Sample time</h2>
            {renderSampleTime()}
          </div>
        </div>
      );

    const openSignUpDialog = () => {
      showSignupDialogFromSourcing(
        WORK_WITH_FACTORIES_DESCRIPTION,
        getCreatorHubProductDetailsPagePath(item.id, 'source'),
        'product_details_page',
      );
    };

    const ActionButtonSection = () => (
      <div className="action-button-container">
        {!isServices && showNumericalInputs && (
          <div
            className={cN('btn order', {
              disabled: !canAddToCart,
            })}
            onClick={openSignup}
            ref={ref as LegacyRef<HTMLDivElement>}
          >
            <Image src="/images/sourcing/add-sample-to-cart.svg" width={14} height={14} alt="add to cart" />
            <span>{isServices ? 'Order service' : 'Order stock samples'}</span>
          </div>
        )}
        {!displayProductRunCard && (
          <>
            <div className={'btn chat-with-supplier-btn'} onClick={openSignUpDialog}>
              <ChatBubbleIcon width={12} height={12} />
              <span>Chat with the supplier</span>
            </div>
            <div className={'btn highlight'} onClick={openSignUpDialog}>
              <TeamMeetingIcon />
              <span>Start a custom project</span>
            </div>
            {isServices ? (
              <span className="disclaimer">
                Depending on the details of your service, the starting price can change. Contact{' '}
                {item?.catalogCompany?.name} to get a quote.
              </span>
            ) : (
              <span className="disclaimer">
                Contact {item?.catalogCompany?.name} to place a custom sample or production order.
              </span>
            )}
          </>
        )}
      </div>
    );

    return (
      <div
        className={cN('sample-builder-section', { 'sample-builder-section-mobile': isMobile })}
        id={SAMPLE_QUANTITY_SELECTOR_ID}
      >
        <div className="inner">
          <ProductTitle title={isServices ? item?.name : 'Order samples'} />
          <YouWillReceive />
          {!sampleTimeRef?.current && <SamplePricing />}
          {showNumericalInputs && !isEmpty(customizationArray) && (
            <>
              {map(customizationArray, (customization, i) => (
                <VariantCustomization
                  key={i}
                  customization={customization}
                  showQtySelector={i === customizationArray.length - 1}
                />
              ))}
            </>
          )}
          <Description />
          <SelectedVariantsSection />
          <ActionButtonSection />
        </div>
      </div>
    );
  },
);

export default SampleBuilderSection;
